import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { fetchLeadRevenuePipeline } from '@src/api/actions/dashboard/leadRevenue';

const fmtUsd = (v) =>
  v == null
    ? '—'
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const monthLabel = (ym) =>
  new Date(`${ym}-01T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });

/**
 * Predicted revenue pipeline — the client-facing value story for lead quality.
 *
 * Cohort-level ONLY (monthly): "your June leads carry $X in predicted revenue,
 * $Y realized so far, N% still maturing". Deliberately no per-lead dollars —
 * we send every lead, and lead-level prices would invite cherry-picking.
 * Renders nothing when the model has no data for this client (unmodeled
 * verticals, brand-new client), so the dashboard stays clean.
 */
function PredictedRevenueCard({ clientId }) {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchLeadRevenuePipeline({ client_id: clientId, months: 6 })
      .then((resp) => {
        if (!cancelled) setData(resp?.data || resp);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const months = useMemo(
    () => (data?.months || []).filter((m) => m.n_leads > 0),
    [data]
  );
  const trend = useMemo(
    () =>
      (data?.trend || [])
        .filter((w) => w.avg_predicted_per_lead != null)
        .map((w) => ({
          week: w.week.slice(5), // MM-DD
          value: w.avg_predicted_per_lead,
        })),
    [data]
  );

  const current = months.length ? months[months.length - 1] : null;

  if (loading || !clientId || months.length === 0) return null;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          sx={{ mb: 1.5 }}
          spacing={1}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <TrendingUpIcon color="primary" fontSize="small" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Predicted Revenue Pipeline
            </Typography>
            <Tooltip
              title="Every lead we generate is scored for expected revenue using your actual close history and the lead's own attributes. Sales typically land within 30 days, so recent months are still maturing."
              arrow
            >
              <Chip label="beta" size="small" variant="outlined" />
            </Tooltip>
          </Stack>
          {current && (
            <Typography variant="body2" color="text.secondary">
              {monthLabel(current.month)}: <b>{current.n_leads.toLocaleString()}</b> leads
              carrying <b>{fmtUsd(current.predicted_revenue)}</b> in predicted revenue
            </Typography>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
          <Box sx={{ flex: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Leads</TableCell>
                  <TableCell align="right">Predicted revenue</TableCell>
                  <TableCell align="right">Realized to date</TableCell>
                  <TableCell align="right">Maturity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {months.map((m) => {
                  const matured = m.pct_matured >= 1;
                  return (
                    <TableRow key={m.month}>
                      <TableCell>{monthLabel(m.month)}</TableCell>
                      <TableCell align="right">{m.n_leads.toLocaleString()}</TableCell>
                      <TableCell align="right">{fmtUsd(m.predicted_revenue)}</TableCell>
                      <TableCell align="right">
                        {m.realized_net_to_date > 0 || matured
                          ? fmtUsd(m.realized_net_to_date)
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          variant={matured ? 'filled' : 'outlined'}
                          color={matured ? 'default' : 'info'}
                          label={
                            matured
                              ? 'matured'
                              : `${Math.round((m.pct_matured || 0) * 100)}% matured`
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {trend.length >= 3 && (
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="caption" color="text.secondary">
                Lead quality trend (avg predicted $ / lead, weekly)
              </Typography>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <XAxis
                    dataKey="week"
                    tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                    tickLine={false}
                    axisLine={{ stroke: theme.palette.divider }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `$${v}`}
                    width={44}
                  />
                  <RechartsTooltip
                    formatter={(v) => [fmtUsd(v), 'Avg predicted / lead']}
                    labelFormatter={(l) => `Week of ${l}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Predicted revenue is a model estimate from your own historical results; recent
          months are incomplete until sales mature (~{data?.maturity_days ?? 30} days).
        </Typography>
      </CardContent>
    </Card>
  );
}

export default memo(PredictedRevenueCard);
